import { useQuery } from '@tanstack/react-query';
import type { CompanyDocuments } from 'api';
import { DocumentsService } from 'api';
import {
  FIRM_DOCUMENT_MEASUREMENT_DATE_DOCUMENTS_QUERY_KEY,
  FIRM_DOCUMENT_MEASUREMENT_DATE_DOCUMENTS_QUERY_STALE_TIME_MS,
} from './useFirmDocumentMeasurementDateDocumentsQuery.constants';
import type {
  FirmDocumentMeasurementDateDocuments,
  UseFirmDocumentMeasurementDateDocumentsQueryParams,
} from './useFirmDocumentMeasurementDateDocumentsQuery.types';

const isCompanyDocumentsResponse = (value: unknown): value is CompanyDocuments => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const response = value as Partial<CompanyDocuments>;

  return Boolean(response.measurement_date) && Array.isArray(response.folders);
};

const getFirmDocumentMeasurementDateDocuments = async (
  measurementDateId: number
): Promise<FirmDocumentMeasurementDateDocuments> => {
  const response = await DocumentsService.apiDocumentsRead(String(measurementDateId));

  return isCompanyDocumentsResponse(response) ? response : null;
};

export const useFirmDocumentMeasurementDateDocumentsQuery = ({
  companyId = null,
  enabled = true,
  measurementDateId = null,
}: UseFirmDocumentMeasurementDateDocumentsQueryParams) => {
  const resolvedMeasurementDateId = measurementDateId ?? 0;

  return useQuery({
    enabled: Boolean(companyId && resolvedMeasurementDateId && enabled),
    queryFn: () => getFirmDocumentMeasurementDateDocuments(resolvedMeasurementDateId),
    queryKey: [
      FIRM_DOCUMENT_MEASUREMENT_DATE_DOCUMENTS_QUERY_KEY,
      companyId ?? '',
      resolvedMeasurementDateId || '',
    ],
    staleTime: FIRM_DOCUMENT_MEASUREMENT_DATE_DOCUMENTS_QUERY_STALE_TIME_MS,
  });
};
